import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import CAxios from "../utils/CAxios";
import i18n from "../../translations/i18";
import {
  getCartErrorMessage,
  getCartResponseError,
  isHttpNotFound,
} from "../../utils/cartResponse";
import { extractSavedCart, isEmptySavedCartResponse, SavedCartPayload, CartLinePayload } from "../../utils/cartPersistence";
import { entityIdsEqual } from "../slices/utils";

type CartState = { global: { products: any[]; services: any[]; vehicle?: any; appointment?: any } };

/** Fetch canonical membership, quantities, options and totals from the server. */
async function readServerCart(
  lang: string,
  state: CartState,
  { allowMissing = false }: { allowMissing?: boolean } = {}
): Promise<SavedCartPayload | null> {
  let response;
  try {
    response = await CAxios.get(`my/cart?lang=${lang}`);
  } catch (error) {
    // No draft yet: Odoo answers 404 with an HTML page instead of an empty JSON cart.
    if (allowMissing && isHttpNotFound(error)) return null;
    throw error;
  }
  const error = getCartResponseError(response.data);
  if (error !== undefined) throw new Error(error || i18n.t("services.productCard.addFailed"));
  const cart = extractSavedCart(response.data);
  if (!cart) {
    if (isEmptySavedCartResponse(response.data)) return null;
    throw new Error(i18n.t("services.cartSync.invalidResponse"));
  }
  const enrich = async (line: CartLinePayload, known: any[]): Promise<CartLinePayload> => {
    const previous = known.find(item => entityIdsEqual(item.id, line.id));
    const presentation = { name: previous?.name, image_128: previous?.image_128, image_1920: previous?.image_1920 };
    let details: any = {};
    if (!line.name || (line.lst_price == null && line.price == null && line.price_unit == null)) {
      try {
        const response = await CAxios.get(`services/${encodeURIComponent(String(line.id))}?lang=${lang}`);
        const body = response.data?.result ?? response.data?.data ?? response.data;
        const product = Array.isArray(body) ? body.find(item => entityIdsEqual(item.id, line.id)) : body;
        if (product && entityIdsEqual(product.id, line.id)) {
          // Catalog display fields never replace cart quantities or options.
          details = { name: product.name, image_128: product.image_128, image_1920: product.image_1920,
            lst_price: product.lst_price ?? product.price, lst_price_discount: product.lst_price_discount };
        }
      } catch {
        // Optional display data cannot change the authoritative cart contents.
      }
    }
    const unitPrice = line.lst_price ?? line.price ?? line.price_unit;
    return { ...presentation, ...details, ...line,
      ...(unitPrice != null ? { lst_price: unitPrice, lst_price_discount: line.lst_price_discount ?? unitPrice } : {}),
    };
  };
  const [products, services] = await Promise.all([
    Promise.all(cart.products.map(line => enrich(line, state.global.products))),
    Promise.all(cart.services.map(line => enrich(line, state.global.services))),
  ]);
  const vehicle = cart.vehicle ?? state.global.vehicle;
  const appointment = cart.appointment ?? state.global.appointment;
  return { ...cart, products, services,
    vehicle: entityIdsEqual(vehicle?.id, cart.vehicle_id) ? vehicle : undefined,
    appointment: entityIdsEqual(appointment?.id, cart.appointment_slot_id) ? appointment : undefined,
  };
}

export const toGetMyCart = createAsyncThunk(
  "global/toGetMyCart",
  async (lang: string, { getState, rejectWithValue }) => {
    try {
      return await readServerCart(lang, getState() as CartState, {
        allowMissing: true,
      });
    }
    catch (error) { return rejectWithValue(getCartErrorMessage(error)); }
  }
);

export type SaveCartArgs = SavedCartPayload & {
  feedbackItem?: { id: number | string; type: "product" | "service" };
  presentation?: CartState["global"];
};

/** Confirm every write by reading the canonical server cart. */
export const toSaveMyCart = createAsyncThunk(
  "global/toSaveMyCart",
  async (args: SaveCartArgs, { getState, rejectWithValue }) => {
    const { feedbackItem, presentation, vehicle, appointment, ...cart } = args;
    try {
      const response = await CAxios.post("my/cart", cart);
      const error = getCartResponseError(response.data);
      if (error !== undefined) return rejectWithValue(error);
      const state = getState() as CartState;
      return await readServerCart(i18n.language || "en", {
        global: { ...state.global, ...presentation },
      });
    } catch (error) { return rejectWithValue(getCartErrorMessage(error)); }
  }
);

export const toDeleteMyCart = createAsyncThunk(
  "global/toDeleteMyCart",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await CAxios.delete("my/cart");
      const error = getCartResponseError(response.data);
      if (error !== undefined) return rejectWithValue(error);
      return await readServerCart(i18n.language || "en", getState() as CartState, {
        allowMissing: true,
      });
    } catch (error) {
      if (isHttpNotFound(error)) return null;
      return rejectWithValue(getCartErrorMessage(error));
    }
  }
);

/** Logout waits for queued operations without uploading a stale browser cart. */
export const toSyncMyCartNow = createAsyncThunk(
  "global/toSyncMyCartNow",
  async (_, { dispatch }) => { await dispatch(requestCartSync()); }
);

/** Refresh from the server without uploading browser state. */
export const requestCartSync = createAction("global/requestCartSync");
